import formatMultiTypeDate from "shared/utils/formatMultiTypeDate";
import { InlineDateFormat, InlineSearch } from "./types";

const TOKEN = /\$\{([^{}]*)\}/g;
const KNOWN_TOKEN =
  /^(ORIGIN_AIRPORT|DESTINATION_AIRPORT|NUMBER_OF_ADULTS|NUMBER_OF_CHILDREN|NUMBER_OF_BABIES|DEPARTURE_DATE_[1-4]|RETURN_DATE_[1-4])$/;

export function safeInlineUrl(
  value: unknown,
  image = false,
): string | undefined {
  if (typeof value !== "string" || !value.trim()) return undefined;
  const input = value.trim();
  try {
    const url = new URL(
      image && input.startsWith("//") ? `https:${input}` : input,
    );
    const allowed = image
      ? url.protocol === "https:"
      : ["https:", "http:"].includes(url.protocol);
    if (!allowed || url.username || url.password) return undefined;
    return url.href;
  } catch {
    return undefined;
  }
}

/** Keep the original template: URL serialization would escape the placeholders. */
export function validateInlineUrlTemplate(template: string): string {
  const authority = template.match(/^https?:\/\/[^/?#]+/i)?.[0];
  if (
    !authority ||
    authority.includes("${") ||
    /[\\\u0000-\u0020\u007f]/.test(template)
  ) {
    throw new Error(
      "URL do inline inválida: protocolo e domínio devem ser fixos",
    );
  }
  const masked = template.replace(TOKEN, (_, name: string) => {
    if (!KNOWN_TOKEN.test(name))
      throw new Error("Variável desconhecida na URL do inline");
    return "inline-value";
  });
  if (/[{}]/.test(masked) || !safeInlineUrl(masked)) {
    throw new Error("Template de URL do inline inválido");
  }
  return template;
}

function dateValue(
  dates: readonly string[] | null,
  index: number,
  format: InlineDateFormat,
): string {
  const value = dates?.[index] ?? dates?.[0];
  if (value === undefined) return "";
  if (typeof value !== "string" || !/^\d{4}-\d{2}-\d{2}$/.test(value)) {
    throw new Error("Data de busca inválida para a URL do inline");
  }
  const parsed = new Date(`${value}T00:00:00Z`);
  if (
    Number.isNaN(parsed.getTime()) ||
    parsed.toISOString().slice(0, 10) !== value
  ) {
    throw new Error("Data de busca inválida para a URL do inline");
  }
  return formatMultiTypeDate(value, format);
}

export function resolveInlineUrl(
  template: string,
  search: InlineSearch,
  format: InlineDateFormat,
): string {
  validateInlineUrlTemplate(template);
  const resolved = template.replace(TOKEN, (_, name: string) => {
    let value: string;
    if (name === "ORIGIN_AIRPORT" || name === "DESTINATION_AIRPORT") {
      value = (
        name === "ORIGIN_AIRPORT"
          ? search.originAirport
          : search.destinationAirport
      )
        .trim()
        .toUpperCase();
      if (!/^[A-Z]{3}$/.test(value))
        throw new Error("Aeroporto de busca inválido para o inline");
    } else if (
      name.startsWith("DEPARTURE_DATE_") ||
      name.startsWith("RETURN_DATE_")
    ) {
      value = dateValue(
        name.startsWith("DEPARTURE")
          ? search.departureDates
          : search.returnDates,
        Number(name.slice(-1)) - 1,
        format,
      );
    } else {
      const count =
        name === "NUMBER_OF_ADULTS"
          ? search.adults
          : name === "NUMBER_OF_CHILDREN"
            ? search.children
            : search.babies;
      if (!Number.isSafeInteger(count) || count < 0)
        throw new Error("Quantidade de passageiros inválida para o inline");
      value = String(count);
    }
    return encodeURIComponent(value);
  });
  const url = safeInlineUrl(resolved);
  if (!url || resolved.includes("${"))
    throw new Error("URL final do inline inválida");
  return url;
}
